import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppContext } from '@/contexts/AppContext';
import type { Product, SpecialOffer } from '@/types';
import { ProductService } from '@/services/domain/ProductService';
import type { Category as ApiCategory, Product as ApiProduct, ProductFilters, Pagination } from '@/types/api';

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const CartIcon = () => (
  <svg
    aria-hidden="true"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="cartStrokeGradient" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="1" stopColor="#ffe3c7" />
      </linearGradient>
    </defs>
    <path
      d="M6.4 8h12.2l-1.3 6a1.6 1.6 0 0 1-1.55 1.26H9.92A1.6 1.6 0 0 1 8.37 14L6.4 8Z"
      fill="currentColor"
      fillOpacity="0.22"
    />
    <path
      d="M3 4h2.3l1.9 9.2a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 1.95-1.58L21.5 7H6.2"
      stroke="url(#cartStrokeGradient)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9.4" cy="19.8" r="1.6" fill="currentColor" />
    <circle cx="18.4" cy="19.8" r="1.6" fill="currentColor" />
  </svg>
);

export const CheckIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const splitPriceLabel = (rawPrice: string) => {
  if (!rawPrice) {
    return { currency: 'R$', amount: '0,00' };
  }

  const match = rawPrice.trim().match(/^([^0-9,]+)?\s*(.+)$/);
  if (!match) {
    return { currency: 'R$', amount: rawPrice.trim() };
  }

  const currency = match[1]?.trim() || 'R$';
  const amount = match[2]?.trim() || '0,00';
  return { currency, amount };
};

const CatshopPage = () => {
  const context = useContext(AppContext);
  
  if (!context) {
    throw new Error('CatshopPage must be used within an AppProvider');
  }
  
  const {
    selectedCategory,
    setSelectedCategory,
    addToCart,
    setIsCartOpen,
    specialOffers,
    calculateDiscountedPrice,
  } = context;

  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [apiCategories, setApiCategories] = useState<ApiCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [addedProducts, setAddedProducts] = useState<Record<number, boolean>>({});
  const feedbackTimersRef = useRef<Record<number, number>>({});

  const categoriesById = useMemo(() => {
    return apiCategories.reduce((map, category) => map.set(category.id, category), new Map<number, ApiCategory>());
  }, [apiCategories]);

  const categorySlugByName = useMemo(() => {
    return apiCategories.reduce((map, category) => map.set(category.name, category.slug), new Map<string, string>());
  }, [apiCategories]);

  const categories = useMemo(() => ['Todos', ...apiCategories.map(category => category.name)], [apiCategories]);

  const mapApiProduct = useCallback((apiProduct: ApiProduct): Product => {
    const categoryName = apiProduct.category_id ? categoriesById.get(apiProduct.category_id)?.name ?? 'Outros' : 'Outros';
    const priceCents = apiProduct.price_cents ?? 0;
    const originalCents = apiProduct.original_price_cents ?? priceCents;
    const hasDiscount = originalCents > priceCents;
    const discountPercentage = hasDiscount
      ? Math.round(((originalCents - priceCents) / originalCents) * 100)
      : 0;

    return {
      id: apiProduct.id,
      name: apiProduct.name,
      description: apiProduct.description ?? '',
      image: '🐾',
      imageUrl: apiProduct.image_url,
      price: apiProduct.price_formatted ?? formatCurrency(priceCents / 100),
      category: categoryName,
      priceNumber: apiProduct.price_reais ?? priceCents / 100,
      inStock: apiProduct.is_available ?? (apiProduct.stock_quantity ?? 0) > 0,
      discount: hasDiscount ? discountPercentage : undefined,
      isSpecialOffer: apiProduct.offer_applied ?? false,
      originalPrice: hasDiscount ? originalCents / 100 : undefined,
      appliedDiscount: hasDiscount ? discountPercentage : undefined,
    };
  }, [categoriesById]);

  useEffect(() => {
    let active = true;

    const fetchCategories = async () => {
      try {
        const data = await ProductService.categories();
        if (!active) return;
        setApiCategories(data);
      } catch (err) {
        console.error('Erro ao carregar categorias', err);
        if (!active) return;
        setApiCategories([]);
      }
    };

    fetchCategories();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const filters: ProductFilters = {
          per_page: 100,
          active_only: true,
        };

        if (selectedCategory !== 'Todos') {
          const categorySlug = categorySlugByName.get(selectedCategory);
          if (categorySlug) {
            filters.category = categorySlug;
          }
        }

        const response: Pagination<ApiProduct> = await ProductService.list(filters);
        if (!active) return;

        const mapped = response.data.map((item: ApiProduct) => mapApiProduct(item));
        setCatalogProducts(mapped);
      } catch (err) {
        console.error('Erro ao carregar produtos', err);
        if (!active) return;
        setError('Não foi possível carregar os produtos no momento.');
        setCatalogProducts([]);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      active = false;
    };
  }, [selectedCategory, categorySlugByName, mapApiProduct]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'Todos') {
      return catalogProducts;
    }
    return catalogProducts.filter(product => product.category === selectedCategory);
  }, [catalogProducts, selectedCategory]);

  const activeOffers = useMemo(() => {
    const now = new Date();
    return specialOffers.filter((offer: SpecialOffer) =>
      offer.isActive &&
      new Date(offer.startDate) <= now &&
      new Date(offer.endDate) >= now
    );
  }, [specialOffers]);

  const offerProducts = useMemo(() => {
    return catalogProducts.filter(product => {
      if (product.isSpecialOffer) return true;
      return activeOffers.some(offer => offer.productId === product.id);
    });
  }, [activeOffers, catalogProducts]);

  const getPriceInfo = useCallback((product: Product) => {
    if (product.originalPrice && product.discount && product.discount > 0) {
      return {
        original: formatCurrency(product.originalPrice),
        final: product.price,
        discount: product.discount,
      };
    }

    if (product.discount && product.discount > 0) {
      return {
        original: product.price,
        final: calculateDiscountedPrice(product.price, product.discount),
        discount: product.discount,
      };
    }

    return {
      original: undefined,
      final: product.price,
      discount: undefined,
    };
  }, [calculateDiscountedPrice]);

  const renderProductImage = useCallback((product: Product) => {
    if (product.imageUrl) {
      return <img src={product.imageUrl} alt={product.name} loading="lazy" />;
    }
    return <span>{product.image}</span>;
  }, []);

  useEffect(() => {
    return () => {
      Object.values(feedbackTimersRef.current).forEach(timerId => {
        window.clearTimeout(timerId);
      });
    };
  }, []);

  const handleAddToCart = useCallback(
    (product: Product) => {
      if (product.inStock === false) {
        return;
      }

      addToCart(product);
      setAddedProducts(prev => ({
        ...prev,
        [product.id]: true,
      }));

      if (feedbackTimersRef.current[product.id]) {
        window.clearTimeout(feedbackTimersRef.current[product.id]);
      }

      feedbackTimersRef.current[product.id] = window.setTimeout(() => {
        setAddedProducts(prev => {
          const updated = { ...prev };
          delete updated[product.id];
          return updated;
        });
        delete feedbackTimersRef.current[product.id];
      }, 1600);

      setFeedbackMessage(`${product.name} adicionado ao carrinho.`);
    },
    [addToCart],
  );

  const CompactOffersCarousel = () => {
    if (isLoading || offerProducts.length === 0) return null;
    
    return (
      <div className="compact-offers-carousel">
        <h3>Ofertas Especiais</h3>
        <div className="compact-products-grid">
          {offerProducts.map(product => {
            const adminOffer = activeOffers.find((offer: SpecialOffer) => offer.productId === product.id);
            const discountPercentage = adminOffer ? adminOffer.discountPercentage : (product.discount || 0);
            const priceInfo = getPriceInfo(product);
            const currentPriceLabel = priceInfo.final;
            const priceParts = splitPriceLabel(currentPriceLabel);
            const offerTitle = adminOffer ? adminOffer.title : 'Oferta Especial';
            
            return (
              <div key={product.id} className={`compact-product-card offer-highlight ${!(product.inStock ?? true) ? 'out-of-stock' : ''}`}>
                <div className="offer-badge" title={adminOffer?.description || 'Produto em oferta'}>
                  {adminOffer ? `${adminOffer.discountPercentage}% OFF` : 'Oferta'}
                </div>
                {adminOffer && (
                  <div className="offer-title-badge">{offerTitle}</div>
                )}
                <div className="product-media">
                  <div className="product-media__frame">
                    <div className="product-media__content">
                      {renderProductImage(product)}
                    </div>
                  </div>
                </div>
                <div className="product-body">
                  <h3 className="product-title">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                </div>
                <div className="product-footer">
                  <div className="product-price">
                    <div className="product-price__current">
                      <span className="product-price__currency">{priceParts.currency}</span>
                      <span className="product-price__amount">{priceParts.amount}</span>
                    </div>
                    {(priceInfo.original || discountPercentage > 0) && (
                      <div className="product-price-details">
                        {priceInfo.original && (
                          <span className="original-price">{priceInfo.original}</span>
                        )}
                        {discountPercentage > 0 && (
                          <span className="discount-badge">-{discountPercentage}%</span>
                        )}
                      </div>
                    )}
                  </div>
                  {adminOffer && adminOffer.description && (
                    <div className="offer-description-mini">{adminOffer.description}</div>
                  )}
                  {!(product.inStock ?? true) && (
                    <div className="out-of-stock-badge">❌ Sem Estoque</div>
                  )}
                  <div className="product-actions">
                    <button
                      className={`add-to-cart-button ${addedProducts[product.id] ? 'added' : ''}`}
                      onClick={() => handleAddToCart(product)}
                      disabled={product.inStock === false}
                      aria-label={addedProducts[product.id]
                        ? `Produto ${product.name} adicionado.`
                        : `Adicionar ${product.name} ao carrinho`
                      }
                      type="button"
                    >
                      <span className="button-content">
                        {addedProducts[product.id] ? <CheckIcon /> : <CartIcon />}
                        <span className="button-label" aria-live="polite">
                          {product.inStock !== false ? (addedProducts[product.id] ? 'Adicionado' : 'Adicionar ao Carrinho') : 'Indisponível'}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className="catshop">
      <div className="container">
        <h2>Catshop - Produtos Exclusivos</h2>
        <p className="section-subtitle">Tudo que seu gatinho precisa em um só lugar</p>

        <CompactOffersCarousel />

        <div className="category-filters">
          {categories.map(category => (
            <button
              key={category}
              className={selectedCategory === category ? 'active' : ''}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="products-grid">
          {isLoading ? (
            <div className="loading-state">Carregando produtos...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : filteredProducts.length === 0 ? (
            <div className="empty-state">Nenhum produto encontrado nesta categoria.</div>
          ) : (
            filteredProducts.map(product => {
              const priceInfo = getPriceInfo(product);
              const currentPriceLabel = priceInfo.final;
              const priceParts = splitPriceLabel(currentPriceLabel);

              return (
                <div key={product.id} className={`product-card ${!(product.inStock ?? true) ? 'out-of-stock' : ''}`}>
                  <div className="product-media">
                    <div className="product-media__frame">
                      <div className="product-media__content">
                        {renderProductImage(product)}
                      </div>
                    </div>
                  </div>
                  <div className="product-body">
                    <h3 className="product-title">{product.name}</h3>
                    <p className="product-description">{product.description}</p>
                  </div>
                  <div className="product-footer">
                    <div className="product-price">
                      <div className="product-price__current">
                        <span className="product-price__currency">{priceParts.currency}</span>
                        <span className="product-price__amount">{priceParts.amount}</span>
                      </div>
                      {(priceInfo.original || priceInfo.discount) && (
                        <div className="product-price-details">
                          {priceInfo.original && (
                            <span className="original-price">{priceInfo.original}</span>
                          )}
                          {priceInfo.discount && (
                            <span className="discount-badge">-{priceInfo.discount}%</span>
                          )}
                        </div>
                      )}
                    </div>
                    {!(product.inStock ?? true) && (
                      <div className="out-of-stock-badge">❌ Sem Estoque</div>
                    )}
                    <div className="product-actions">
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`add-to-cart-button ${addedProducts[product.id] ? 'added' : ''}`}
                        disabled={product.inStock === false}
                        aria-label={addedProducts[product.id]
                          ? `Produto ${product.name} adicionado.`
                          : `Adicionar ${product.name} ao carrinho`
                        }
                        type="button"
                      >
                        <span className="button-content">
                          {addedProducts[product.id] ? <CheckIcon /> : <CartIcon />}
                          <span className="button-label" aria-live="polite">
                            {product.inStock !== false ? (addedProducts[product.id] ? 'Adicionado' : 'Adicionar ao Carrinho') : 'Indisponível'}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className="sr-only" role="status" aria-live="polite">
        {feedbackMessage}
      </div>
    </section>
  );
};

export default CatshopPage;